import { toJS } from 'mobx';
import { useTranslation } from 'react-i18next';

import type {
  IEmailABTestSettings,
  MailrailBaseEmailCampaign,
  MailrailSingleSendEmailCampaign,
} from '@feathr/blackbox';
import { EMAIL_AB_TEST_DEFAULTS } from '@feathr/blackbox';
import { useFlags } from '@feathr/extender/state';
import { isEmailABTestAvailable } from '@feathr/extender/utils/emailABTest';

export interface IUseMailrailABTestResult {
  abTest: IEmailABTestSettings;
  abTestPublishErrors: string[];
  isABTestAvailable: boolean;
  setABTest: (abTest: IEmailABTestSettings) => void;
}

export function useMailrailABTest(campaign: MailrailBaseEmailCampaign): IUseMailrailABTestResult {
  const flags = useFlags();
  const { t } = useTranslation();
  const singleSend = campaign as MailrailSingleSendEmailCampaign;
  const abTest: IEmailABTestSettings = {
    ...EMAIL_AB_TEST_DEFAULTS,
    ...toJS(singleSend.get('ab_test')),
  };
  const isABTestAvailable = isEmailABTestAvailable(flags, campaign);

  function setABTest(next: IEmailABTestSettings): void {
    // Set clean so the save payload leaves ab_test out until Feathr/anhinga#9460 accepts it.
    singleSend.set({ ab_test: next }, false);
  }

  // ab_test isn't saved yet, so an enabled test would silently go out as a regular single send.
  const abTestPublishErrors =
    isABTestAvailable && abTest.is_enabled
      ? [t("A/B tests can't be sent yet. Turn off the A/B test to publish.")]
      : [];

  return {
    abTest,
    abTestPublishErrors,
    isABTestAvailable,
    setABTest,
  };
}
